import createCache from '@emotion/cache';
import { CacheProvider, css, Global } from '@emotion/react';
import styled from '@emotion/styled';
import type { Preview } from '@storybook/nextjs-vite';
import MockDate from 'mockdate';
import PropTypes from 'prop-types';
import React from 'react';
import { withContext } from 'recompose';
import seedrandom from 'seedrandom';
import { action } from 'storybook/actions';
import { makeStripeDecorator } from './stripeDecorator';

import isChromatic from 'chromatic/isChromatic';
import { MotionGlobalConfig } from 'framer-motion';
import { Collapse } from '../services/webapp/components/Collapse';
import LazyLoad from '../services/webapp/components/LazyLoad';
import { LazyLoadedVideo } from '../services/webapp/components/LazyLoadedVideo';
import { GlobalStyle } from '../services/webapp/components/shared/global';
import { background } from '../services/webapp/components/shared/styles';
import { AuthCallbackContext } from '../services/webapp/lib/AuthCallbackContext';
import makeApolloDecorator from '../services/webapp/lib/make-apollo-decorator';
import { setNoCredentials } from '../services/webapp/lib/test-metadata';
import { KeyboardShortcutProvider } from '../services/webapp/lib/useKeyboardShortcuts';
import { AnalyticsContext } from '../services/webapp/lib/withAnalytics';

import { defaultFeatures, FeatureFlagsProvider } from '../services/webapp/hooks/useFeatureFlag';
import { getTimezoneOffset } from '../services/webapp/lib/getTimezoneOffset';
import { mocked, sb } from 'storybook/test';

sb.mock('../services/webapp/lib/getTimezoneOffset.ts', { spy: true });

const getCustomGlobalStyle = (context) => {
  const [rootId] = context.id.split('-', 1);
  const bgColor = ['emails', 'index'].includes(rootId) ? '#fff' : background.app;
  return css`
    /*
    * Prevents jump links from butting against the top
    * of the browser or being hidden behind a fixed header
    */
    html {
      scroll-behavior: smooth;
      scroll-padding-block-start: 5rem;

      @media (min-width: 900px) {
        scroll-padding-block-start: 3rem;
      }
    }

    body {
      background-color: ${bgColor};
    }

    // Some of our layouts rely on all of the root elements taking up full height.
    // See: https://github.com/chromaui/chromatic/pull/4539
    html,
    body,
    #root,
    #storybook-root {
      height: 100%;
    }
  `;
};

// JSDOM has some issues with the Collapse during the smoke tests
Collapse.disabled = typeof jest !== 'undefined';

// Don't use lazyload for chromatic screenshots
LazyLoad.disabled = isChromatic();

// Combination of autoplay and poster (maybe?) causes flake. Will be disabled in capture 7
LazyLoadedVideo.disableAutoPlay = isChromatic();

// We can't use credentialed requests to get images from our CDN. This is due to the fact that our
// stories are hosted on xyz.chromatic.com, which as *user writable* domain. So we need to be super careful.
// It's OK though. All our stories use publicly accessible images so we don't need to pass credentials through
setNoCredentials(true);

if (isChromatic()) {
  MockDate.set('April 25, 2018 13:12:59');
}

seedrandom('make-random-predictable', { global: true });

const analytics = { trackBundle: action('track-analytics') };
const AnalyticsWrapper = withContext(
  {
    analytics: PropTypes.shape({
      trackBundle: PropTypes.func.isRequired,
    }).isRequired,
  },
  () => ({ analytics })
)(({ children }: React.PropsWithChildren) => children);

// AnalyticsWrapper & recompose logic should be deleted eventually once all
// dependencies of this logic have been updated. Favor context for new work.
// https://github.com/chromaui/chromatic/issues/3572
const decorators: Preview['decorators'] = [];

const ModalContainer = styled.div`
  display: contents;
`;
// This wrapper allows us to ensure tooltips appear inside the story's canvas
decorators.push((s) => (
  <>
    {s()}
    <ModalContainer id="modal-container" />
  </>
));

// For stories with modals that render outside of the modal container
// (this is common if the story renders the modal immediately)
// use the `minWidth/Height` parameters to force the Chromatic capture to be a certain size
type MinSizeProps = { minWidth?: string; minHeight?: string };
const MinSizeContainer = styled.div<MinSizeProps>`
  width: fit-content;
  min-width: ${(props) => props.minWidth ?? 'auto'};
  height: fit-content;
  min-height: ${(props) => props.minHeight ?? 'auto'};
`;
decorators.push((storyFn, { parameters: { minWidth, minHeight } }) =>
  minWidth || minHeight ? (
    <MinSizeContainer minWidth={minWidth} minHeight={minHeight}>
      {storyFn()}
    </MinSizeContainer>
  ) : (
    storyFn()
  )
);

decorators.push((storyFn) => <AnalyticsWrapper>{storyFn()}</AnalyticsWrapper>);
decorators.push((storyFn) => (
  // @ts-expect-error AnalyticsContext not typed
  <AnalyticsContext.Provider value={{ analytics }}>{storyFn()}</AnalyticsContext.Provider>
));

decorators.push(makeApolloDecorator([]));

// See note in stripeDecorator.js
decorators.push(makeStripeDecorator(true));

decorators.push((storyFn) => (
  <KeyboardShortcutProvider initialShortcuts={{}}>{storyFn()}</KeyboardShortcutProvider>
));

decorators.push((storyFn) => (
  <AuthCallbackContext.Provider
    value={{
      onLoginWithCredentials: async (...args: any[]) => action('onLoginWithCredentials')(...args),
      onLoginWithProvider: async (...args: any[]) => action('onLoginWithProvider')(...args),
      onLogout: async (...args: any[]) => action('onLogout')(...args),
    }}
  >
    {storyFn()}
  </AuthCallbackContext.Provider>
));

const EMOTION_KEY = 'css';

const cache = createCache({ key: EMOTION_KEY });
cache.compat = true;

decorators.push((story, context) => (
  <CacheProvider value={cache}>
    <GlobalStyle />
    <Global styles={getCustomGlobalStyle(context)} />
    {story()}
  </CacheProvider>
));

// Use framer-motion's global config to disable animations for visual tests
MotionGlobalConfig.skipAnimations = isChromatic();
MotionGlobalConfig.useManualTiming = isChromatic();

decorators.push((Story, context) => {
  const { featureFlag } = context.globals;

  const featureFlags = { ...defaultFeatures };
  featureFlags[featureFlag] = true;
  // TODO: Temporarily enable chInteractionTests for all stories before we remove the flag entirely
  featureFlags.chInteractionTests = true;

  return (
    <FeatureFlagsProvider features={featureFlags}>
      <Story />
    </FeatureFlagsProvider>
  );
});

const parameters = {
  backgrounds: {
    options: {
      light: { name: 'light', value: '#fff' },
      dark: { name: 'dark', value: '#171C23' },
    },
  },

  layout: 'fullscreen',

  options: {
    storySort: (a, b) => (a.title === b.title ? 0 : a.id.localeCompare(b.id, { numeric: true })),
  },

  test: {
    dangerouslyIgnoreUnhandledErrors: true,
  },

  viewport: {
    options: {
      xsm: { name: 'XSmall', styles: { width: '320px', height: '100%' } },
      sm: { name: 'Small', styles: { width: '640px', height: '100%' } },
      md: { name: 'Medium', styles: { width: '768px', height: '100%' } },
      lg: { name: 'Large', styles: { width: '1024px', height: '100%' } },
      xl: { name: 'XL', styles: { width: '1280px', height: '100%' } },
      '2xl': { name: '2XL', styles: { width: '1536px', height: '100%' } },
    },
  },

  a11y: {
    // 'todo' - show a11y violations in the test UI only
    // 'error' - fail CI on a11y violations
    // 'off' - skip a11y checks entirely
    test: 'todo',
    options: {
      // prevent axe from hanging for a long time on media assets that will not be loaded
      preload: { assets: ['media'], timeout: 5000 },
    },
    config: {
      rules: [
        // disabling video rules as they are not applicable to our video posters
        {
          id: 'video-caption',
          enabled: false,
        },
        {
          id: 'no-autoplay-audio',
          enabled: false,
        },
      ],
    },
  },
};

const globalTypes = {
  featureFlag: {
    description: 'Account-level feature flag',
    toolbar: {
      title: 'Feature flag',
      dynamicTitle: true,
      icon: 'flag',
      items: [
        { value: '', title: 'Reset' },
        ...Object.keys(defaultFeatures)
          .sort()
          .map((key) => ({
            title: key,
            value: key,
          })),
      ],
    },
  },
};

const preview: Preview = {
  parameters,
  decorators,
  globalTypes,
  // Set the timezone offset to UTC by default for all stories
  async beforeEach() {
    mocked(getTimezoneOffset).mockReturnValue(0);
  },
};
export default preview;

window.addEventListener('unhandledrejection', function (e) {
  console.log('vvv -- Caught handled rejection -- vvv');
  console.log(e.reason);
  console.log(e.reason?.stack);
  console.log('^^^ -- Done in caught unhandled rejection -- ^^^');
  throw e;
});
